import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSupportReport, updateSupportReport } from '../api/supportApi';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ThreadView from '../components/support/ThreadView';
import MainLayout from '../components/layout/MainLayout';
import { useDealerAdmin } from '../contexts/DealerAdminContext';
import {
  canEditReleaseNotes,
  canEscalateSupportReport,
  supportCategoryLabel,
  supportStatusOptions,
} from '../utils/supportAccess';

const SupportReportDetailPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser, userData, userClaims } = useAuth();
  const dealerAdmin = useDealerAdmin();
  const role = userData?.role || userClaims?.role;
  const showReleaseNotes = canEditReleaseNotes(role);
  const canEscalate = canEscalateSupportReport(role, { dealerActive: dealerAdmin.active });
  const statusOptions = supportStatusOptions(false);

  // Fetch support report details
  useEffect(() => {
    fetchReportDetails();
  }, [reportId]);

  const fetchReportDetails = async () => {
    setLoading(true);
    try {
      console.log(`[REPORT-DETAIL] Fetching report with ID: ${reportId}`);
      const data = await getSupportReport(reportId);
      console.log(`[REPORT-DETAIL] Report data received:`, data);
      setReport(data);
      setError(null);
    } catch (err) {
      console.error(`[REPORT-DETAIL] Error fetching report ${reportId}:`, err);
      // Extract more detailed error information if available
      const errorMessage = err.message || 'Unknown error';
      setReport(null);
      setError(`Failed to load support report details: ${errorMessage}`);
      toast.error(`Error: ${errorMessage}`, {
        position: 'top-center',
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Update report status
  const handleStatusChange = async newStatus => {
    try {
      await updateSupportReport(reportId, { status: newStatus });
      setReport({ ...report, status: newStatus });
      toast.success('Status updated successfully');
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('Failed to update status');
    }
  };

  // Toggle release note flag
  const toggleReleaseNote = async () => {
    try {
      const newValue = !report.isReleaseNote;
      await updateSupportReport(reportId, { isReleaseNote: newValue });
      setReport({ ...report, isReleaseNote: newValue });
      toast.success(`Report ${newValue ? 'marked as' : 'removed from'} release notes`);
    } catch (err) {
      console.error('Error updating release note flag:', err);
      toast.error('Failed to update release note status');
    }
  };

  const escalateToSignalBridge = async () => {
    try {
      const updated = await updateSupportReport(reportId, {
        escalate: true,
        escalationStatus: 'escalated',
        escalationReason: 'Escalated by tenant admin',
      });
      setReport({ ...report, ...updated });
      toast.success('Escalated to Signal Bridge support');
    } catch (err) {
      console.error('Error escalating report:', err);
      toast.error('Failed to escalate report');
    }
  };

  // Format date for display
  const formatDate = timestamp => {
    if (!timestamp) return 'N/A';

    // If timestamp is a Firestore timestamp, convert it
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex justify-center my-8">
          <LoadingSpinner />
        </div>
      </MainLayout>
    );
  }

  if (error || !report) {
    return (
      <MainLayout>
        <div className="container mx-auto px-4 py-8">
          <div
            className="bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded mb-4"
            role="alert"
          >
            <p className="font-bold">Error Loading Report</p>
            <p>{error || 'Report not found'}</p>

            {/* Debug information */}
            {/* <div className="mt-4 p-3 bg-gray-100 rounded text-xs">
            <h4 className="font-bold mb-1">Debug Information</h4>
            <p>Report ID: {reportId}</p>
            <p>User: {currentUser?.email || currentUser?.uid || 'Not authenticated'}</p>
            <p>Time: {new Date().toLocaleString()}</p>
          </div> */}
          </div>

          <div className="flex space-x-4">
            <button
              onClick={() => navigate('/support/reports')}
              className="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Back to Reports 0
            </button>

            <button
              onClick={() => fetchReportDetails()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-8">
        {report?.isBackendError && (
          <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4">
            <p className="font-bold">Backend Database Issue</p>
            <p>
              The backend is currently experiencing a database configuration issue. Limited
              information is available.
            </p>
            <p className="mt-2 text-sm">Error: Authentication error: name 'db' is not defined</p>
          </div>
        )}

        <div className="mb-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold">Support Report Details</h1>
          <button
            onClick={() => navigate('/support/reports')}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Back to Reports 1
          </button>
        </div>

        <div className="bg-white shadow-md rounded-lg overflow-hidden mb-6">
          <div className="border-b border-gray-200 px-6 py-4">
            <h2 className="text-xl font-semibold">{report.title}</h2>
            <div className="flex flex-wrap gap-3 mt-2">
              <span
                className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
              ${
                report.type === 'bug'
                  ? 'bg-red-100 text-red-800'
                  : report.type === 'feature'
                    ? 'bg-green-100 text-green-800'
                    : report.type === 'feedback'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
              }`}
              >
                {supportCategoryLabel(report.type)}
              </span>
              <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                Created: {formatDate(report.createdAt)}
              </span>
              {report.updatedAt && report.updatedAt !== report.createdAt && (
                <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                  Updated: {formatDate(report.updatedAt)}
                </span>
              )}
            </div>
          </div>

          <div className="px-6 py-4">
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                Description
              </h3>
              <p className="text-gray-700 whitespace-pre-line">{report.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                  Status
                </h3>
                <select
                  value={report.status}
                  onChange={e => handleStatusChange(e.target.value)}
                  disabled={report.isBackendError}
                  className={`block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md ${report.isBackendError ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                  Escalation
                </h3>
                <p className="text-sm text-gray-700 mb-2">
                  {report.escalationStatus === 'escalated' ? 'Escalated to Signal Bridge' : 'Tenant triage'}
                  {report.supportScope ? ` · ${report.supportScope}` : ''}
                </p>
                {canEscalate && report.escalationStatus !== 'escalated' && !report.isBackendError && (
                  <button
                    type="button"
                    onClick={escalateToSignalBridge}
                    className="bg-amber-600 hover:bg-amber-700 text-white text-sm py-1.5 px-3 rounded"
                  >
                    Escalate to Signal Bridge
                  </button>
                )}
              </div>

              {showReleaseNotes && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                  Release Note
                </h3>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={report.isReleaseNote || false}
                    onChange={toggleReleaseNote}
                    disabled={report.isBackendError}
                    className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${report.isBackendError ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                  <span className="ml-2 text-sm text-gray-700">Include in release notes</span>
                </div>

                {report.isReleaseNote && (
                  <div className="mt-2">
                    <label htmlFor="releaseId" className="block text-sm font-medium text-gray-700">
                      Release ID
                    </label>
                    <input
                      type="text"
                      id="releaseId"
                      value={report.releaseId || ''}
                      onChange={e => updateSupportReport(reportId, { releaseId: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Enter release ID"
                    />
                  </div>
                )}
              </div>
              )}
            </div>

            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                System Information
              </h3>
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="block text-gray-500">Route</span>
                    <span className="text-gray-700">{report.route || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="block text-gray-500">App</span>
                    <span className="text-gray-700">{report.app || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="block text-gray-500">Browser</span>
                    <span className="text-gray-700">{report.browser || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="block text-gray-500">Platform</span>
                    <span className="text-gray-700">{report.platform || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="block text-gray-500">Screen Size</span>
                    <span className="text-gray-700">{report.screenSize || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="block text-gray-500">Scope</span>
                    <span className="text-gray-700">{report.supportScope || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="block text-gray-500">Tenant ID</span>
                    <span className="text-gray-700">{report.tenantId || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="block text-gray-500">Created By</span>
                    <span className="text-gray-700">{report.userId || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Thread Section */}
        <ThreadView reportId={reportId} />
      </div>
    </MainLayout>
  );
};

export default SupportReportDetailPage;
